import { useState } from 'react';
//api
import { getAllUsersAPI } from '../services/usersAPI';
import { getUsersAssignedToJobAPI } from '../services/jobPermissionsAPI';
import { getGroupsUsersAPI } from '../services/groupsAPI'
//context
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';

/**
 * 
 * Gets user name options for dropdowns 
 * @returns {DropdownOption[]} userNameOptions - all users in system
 * @returns {DropdownOption[]} userNameOptionsJob  - all users for a job
 * @returns {DropdownOption[]} userNameOptionsGroup - all users in a group
 * @returns {Function} setUserNameOptions - setter function for userNameOptions - usually to add a newly created user to dropdown
 * @returns {Function} fetchAllUsers 
 * @returns {Function} fetchUsersForJob 
 * @returns {Function} fetchUsersForGroup
 *  
 * @category Hooks - Dropdown Options
 */
const useUserOptions = () => {
    const [userNameOptions, setUserNameOptions] = useState([]);
    const [userNameOptionsJob, setUserNameOptionsJob] = useState([]);
    const [userNameOptionsGroup, setUserNameOptionsGroup] = useState([]);
    
    const { showLoading, hideLoading } = useLoading();
    const { showError } = useError();
    
    //fetch all system users
    const fetchAllUsers = async () => {
        showLoading('Getting users...');
        try {
            const response = await getAllUsersAPI();

            if (!response.success) {
                throw new Error(response.message);
            }

            const users = response.data;
                
            if (users.length > 0) {
                const userOptions = users.map(user => ({
                    value: user.id,
                    label: user.name
                }));
                
                setUserNameOptions(userOptions);
            }

        } catch (error) {
            showError(`Error getting users - ${error.message}`);
        } finally {
            hideLoading();
        }
    };

    //fetch all users for a particular job
    const fetchUsersForJob = async(JobID) => {
        showLoading("Getting users for job...")

        try {
            const response = await getUsersAssignedToJobAPI(JobID);

            if (!response.success) {
                throw new Error(response.message);
            }

            const users = response.data;

            
            const userOptions = users.map(user => ({
                value: user.UserID,
                label: `${user.First_Name} ${user.Last_Name}`
            }));
            
            setUserNameOptionsJob(userOptions);

        } catch (error) {
            showError(`Error getting users for this job - ${error.message}`);
        } finally {
            hideLoading();
        }
    };

        //fetch all users for a particular group
        const fetchUsersForGroup = async(GroupID) => {
            showLoading("Getting users for group...")
    
            try {
                const response = await getGroupsUsersAPI(GroupID);
    
                if (!response.success) {
                    throw new Error(response.message);
                }
    
                const users = response.data;
                
                const userOptions = users.map(user => ({
                    value: user.UserID,
                    label: `${user.First_Name} ${user.Last_Name}`
                }));
                
                setUserNameOptionsGroup(userOptions);
    
            } catch (error) {
                showError(`Error getting users for this group - ${error.message}`);
            } finally {
                hideLoading();
            }
        };
    //fetch all users for a particular group
    const fetchUsersForSpeedNotifications = async() => {
        showLoading("Getting users...")

        try {
            const response = await getAllUsersAPI("getUserGroups");

            if (!response.success) {
                throw new Error(response.message);
            }

            const users = response.data.filter(user => 
                user.groups.some(group => 
                    group.Group_Name === "Project Manager" ||
                    group.Group_Name === "Supervisor" ||
                    group.Group_Name === "Admin" 
                ))

             const userOptions = users.map(user => ({
                value: user.id,
                label: user.name
            }));
            
            setUserNameOptions(userOptions);

        } catch (error) {
            showError(`Error getting users - ${error.message}`);
        } finally {
            hideLoading();
        }
    };
    
    return { 
        userNameOptions, 
        userNameOptionsJob, 
        userNameOptionsGroup, 
        setUserNameOptions, 
        fetchAllUsers, 
        fetchUsersForJob, 
        fetchUsersForGroup, 
        fetchUsersForSpeedNotifications 
    };
};

export default useUserOptions;